import {useEffect,useRef,useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowDown,ArrowUpRight} from 'lucide-react';
import {useContent} from '../hooks/useContent';
import {SourceNote,ClosingCTA,ArrowLink} from '../components/UI';
import {MotionBackdrop} from '../components/EditorialTitle';
import {TechText} from '../components/TechText';
import {AnimatedNumber} from '../components/TechEffects';
import {NibelcLogo3D} from '../components/NibelcLogo3D';
import {ProfileHub} from '../components/ProfileHub';
import '../styles/about-tech.css';

const EVENTS=[
 ['1968','Khởi nguồn một hành trình lớn','Tiền thân công ty Nhà nước được thành lập từ năm 1968.'],
 ['2021','Hành trình nhân lực quốc tế','Kho tư liệu ghi nhận hoạt động và các đợt xuất cảnh tại nhiều thị trường.'],
 ['2025','Kết nối đào tạo và đối tác','Tư liệu hợp tác ITW Berlin và hoạt động kết nối doanh nghiệp quốc tế.'],
 ['2026','Tiếp nối cùng cộng đồng','Hoạt động tư vấn việc làm và đào tạo tại các địa phương tiếp tục được công bố.']
];
function useReveal(){
 const ref=useRef<HTMLElement>(null);
 useEffect(()=>{const el=ref.current;if(!el)return;const io=new IntersectionObserver(es=>{if(es[0].isIntersecting){el.classList.add('is-in');io.disconnect()}},{threshold:.15});io.observe(el);return()=>io.disconnect()},[]);
 return ref;
}
function Section({id,className='',children}:{id?:string;className?:string;children:React.ReactNode}){const ref=useReveal();return <section id={id} ref={ref} className={'at-sec '+className}>{children}</section>}

export function AboutTech(){
 const data=useContent(),[sel,setSel]=useState(0),[docs,setDocs]=useState<{id:string;title:string;path:string}[]>([]);
 useEffect(()=>{const c=new AbortController();fetch('/data/documents.json',{signal:c.signal}).then(r=>r.ok?r.json():[]).then(setDocs).catch(()=>{});return()=>c.abort()},[]);
 const progress=(sel/(EVENTS.length-1))*100;
 return <div className="at-page">
  {/* ===== HERO ===== */}
  <section className="at-hero">
   <MotionBackdrop variant="craft" className="at-backdrop"/>
   <i className="at-hero-grid" aria-hidden="true"/><i className="at-hero-scan" aria-hidden="true"/>
   <div className="at-hero-copy">
    <Link to="/" className="at-crumb">NIBELC GROUP <span>/ VỀ CHÚNG TÔI</span></Link>
    <div className="at-hero-logo"><NibelcLogo3D name="nibelc-logo"/></div>
    <TechText className="at-eyebrow" text="// OUR STORY · SINCE 1968" speed={26} caret/>
    <h1 className="at-title"><TechText as="span" className="at-l1" text="Một nền tảng vững." speed={34}/><TechText as="span" className="at-l2" text="Một tầm nhìn đi xa." speed={34} delay={700}/></h1>
    <p className="at-lead">Kết nối con người, đào tạo và năng lực công nghiệp. Từ Việt Nam, cùng kiến tạo những hành trình quốc tế.</p>
    <a className="at-cta" href="#hanh-trinh"><span>Khám phá hành trình</span><ArrowDown size={18}/></a>
   </div>
   <div className="at-hero-year" aria-hidden="true"><b>1968</b><span>Mốc thành lập tiền thân</span></div>
  </section>

  {/* ===== FACT RAIL ===== */}
  <div className="at-facts">
   {[[1968,'Năm thành lập tiền thân','year'],[data.company.entities.length,'Đơn vị trong tư liệu hệ sinh thái','n'],[data.services.length,'Lĩnh vực năng lực','n'],[10,'Công ty thành viên trong và ngoài nước','plus']].map(([v,l,k])=>
    <div key={String(l)} className="at-fact"><strong><AnimatedNumber value={Number(v)} format={k==='year'?'year':'number'}/>{k==='plus'&&<em>+</em>}</strong><span>{l}</span></div>)}
  </div>

  {/* ===== INTRO + ARCHIVE FRAME ===== */}
  <Section id="cau-chuyen" className="at-intro">
   <div className="at-intro-text">
    <TechText className="at-kicker" text="01 // CÂU CHUYỆN NIBELC" speed={26}/>
    <h2>Không ngừng phát triển.<br/><em>Cùng một giá trị cốt lõi.</em></h2>
    <p>{data.company.legalName} có tiền thân thành lập năm 1968. Nguồn corporate công bố NIBELC phát triển thành Group với trên 10 công ty thành viên trong và ngoài nước.</p>
    <p>Cung ứng và quản lý nhân lực, đào tạo nghề, xây dựng, cơ khí, hạ tầng, thiết bị và vật liệu — những năng lực kết nối trong cùng một hệ sinh thái.</p>
    <ArrowLink to="/he-sinh-thai">Khám phá hệ sinh thái</ArrowLink>
    <SourceNote sources={data.company.sources}/>
   </div>
   <figure className="at-archive">
    <div className="at-archive-frame"><i className="at-b at-b-tl"/><i className="at-b at-b-tr"/><i className="at-b at-b-bl"/><i className="at-b at-b-br"/><img src={data.image.industry} alt="Ảnh tư liệu hoạt động công nghiệp NIBELC giai đoạn sau" loading="lazy"/><span className="at-archive-scan" aria-hidden="true"/><span className="at-archive-tag">ARCHIVE · TƯ LIỆU</span></div>
    <figcaption>Ảnh tư liệu hoạt động NIBELC giai đoạn sau; 1968 là mốc thành lập tiền thân.</figcaption>
   </figure>
  </Section>

  {/* ===== PAST → FUTURE BAND (ChatGPT image) ===== */}
  <Section className="at-continuum">
   <div className="at-continuum-media"><img src="/assets/generated/about-heritage.webp" alt="Từ xưởng cơ khí thập niên 1960 đến khuôn viên công nghiệp hiện đại" loading="lazy"/><i className="at-seam" aria-hidden="true"/><span className="at-era at-era-a">1968</span><span className="at-era at-era-b">NGÀY NAY</span></div>
   <div className="at-continuum-copy"><TechText className="at-kicker" text="02 // TỪ NỀN TẢNG ĐẾN TƯƠNG LAI" speed={26}/><h2>Nối liền quá khứ,<br/><em>kiến tạo ngày mai.</em></h2><p>Hơn nửa thế kỷ tích lũy kinh nghiệm công nghiệp và con người, đưa nền tảng ấy vào các hành trình nhân lực và đào tạo quốc tế.</p></div>
  </Section>

  {/* ===== TIMELINE ===== */}
  <Section id="hanh-trinh" className="at-timeline">
   <TechText className="at-kicker" text="03 // HÀNH TRÌNH PHÁT TRIỂN" speed={26}/>
   <h2>Dấu mốc tạo nền tảng.<br/><em>Tầm nhìn mở tương lai.</em></h2>
   <p className="at-note">Dấu mốc tổng hợp từ kho tư liệu NIBELC; không thay thế hồ sơ pháp lý hoặc xác nhận tình trạng hiện hành.</p>
   <div className="at-rail" role="tablist" aria-label="Dấu mốc lịch sử"><i className="at-rail-line"><b style={{width:progress+'%'}}/></i>
    {EVENTS.map(([y,t],i)=><button key={y} role="tab" type="button" aria-selected={sel===i} id={'at-tab-'+i} aria-controls="at-panel" className={(sel===i?'is-on ':'')+(i<=sel?'is-past':'')} onClick={()=>setSel(i)} onMouseEnter={()=>setSel(i)}><span className="at-dot"/><strong>{y}</strong><small>{t}</small></button>)}
   </div>
   <div className="at-panel" id="at-panel" role="tabpanel" aria-labelledby={'at-tab-'+sel} key={sel}><b className="at-panel-year">{EVENTS[sel][0]}</b><div><h3>{EVENTS[sel][1]}</h3><p>{EVENTS[sel][2]}</p><SourceNote sources={data.company.sources}/></div></div>
  </Section>

  {/* ===== TRUST ===== */}
  <Section id="ho-so" className="at-trust">
   <div><TechText className="at-kicker" text="04 // NỀN TẢNG NIỀM TIN" speed={26}/><h2>Năng lực được xây dựng.<br/><em>Niềm tin được gìn giữ.</em></h2><p>Thông tin từ tư liệu nguồn, minh bạch về thời điểm và phạm vi xác minh.</p></div>
   <div className="at-license"><SourceNote sources={data.company.sources}/>
    {docs.length>0&&<details className="at-docs"><summary>Tài liệu tải về từ kho nguồn <span>{docs.length}</span></summary><div>{docs.slice(0,16).map((d,i)=><a key={d.id+'-'+i} href={d.path} target="_blank" rel="noreferrer">{d.title}<ArrowUpRight size={14}/></a>)}</div></details>}
   </div>
  </Section>

  <Section id="profile" className="at-profile"><ProfileHub/></Section>

  {/* ===== PEOPLE ===== */}
  <Section className="at-people">
   <figure className="at-people-photo"><i className="at-b at-b-tl"/><i className="at-b at-b-br"/><img src={data.image.training} alt="Tư liệu đội ngũ và đào tạo NIBELC" loading="lazy"/></figure>
   <div><TechText className="at-kicker" text="05 // CON NGƯỜI & VĂN HÓA" speed={26}/><h2>Giá trị bắt đầu<br/>từ <em>con người.</em></h2><p>Đào tạo, hoạt động cộng đồng và hỗ trợ người lao động ở nước ngoài — những câu chuyện làm nên NIBELC.</p><ArrowLink to="/tin-tuc" className="button gold">Những câu chuyện NIBELC</ArrowLink></div>
  </Section>
  <ClosingCTA/>
 </div>;
}
